import {Component} from '@angular/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {MergeModal} from './merge-modal/merge-modal';

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.css']
})
export class AppComponent {
	title = 'princity-i18n';

	private originalMap = new Map();
	private entriesMap: Map<string, {[key: string]: string}> = new Map();
	keys: string[] = [];
	diffMap: Map<string, any> = new Map();
	page = 0;
	tableParams = {
		page: 1,
		limit: 10,
		fullText: '',
		entriesCount: 0,
		showEmpty: false
	};
	keyVisibilityMap: Map<string, boolean> = new Map();
	keySearchabilityMap: Map<string, boolean> = new Map();

	constructor(private modalService: NgbModal) {
	}

	get entries(): {[key: string]: string}[] {
		const start = (this.tableParams.page - 1) * this.tableParams.limit;
		const end = start + this.tableParams.limit;
		const entries = Array.from(this.entriesMap.values()).filter(
			entry => Object.keys(entry).filter(key => this.keySearchabilityMap.get(key))
				.some(
					key => (!this.tableParams.fullText || entry[key].toLowerCase().includes(this.tableParams.fullText.toLowerCase()))
						&& (!this.tableParams.showEmpty || !entry[key])
				)
		);
		this.tableParams.entriesCount = entries.length;
		return entries.slice(start, end);
	}

	get visibleKeys(): string[] {
		return this.keys.filter(key => this.keyVisibilityMap.get(key));
	}

	loadFile(event: any): void {
		this.originalMap.clear();
		this.entriesMap.clear();
		this.diffMap.clear();
		if (!event.target.files[0]) {
			return;
		}
		this.convertFile(event.target.files[0], result => {
			result.entries.forEach(entry => {
				this.entriesMap.set(entry.id, entry);
				this.originalMap.set(entry.id, Object.assign({}, entry));
			});
			this.keys = result.keys;
			this.keys.forEach(key => {
				this.keyVisibilityMap.set(key, true);
				this.keySearchabilityMap.set(key, true);
			});
		});
	}

	mergeFile(event: any): void {
		if (!event.target.files[0]) {
			return;
		}

		this.convertFile(event.target.files[0], mergeFileResult => {
			const modalRef = this.modalService.open(MergeModal);
			modalRef.componentInstance.availableKeys = mergeFileResult.keys;
			modalRef.result.then(selectedKeys => {
				if (!selectedKeys || !selectedKeys.length) {
					return;
				}

				mergeFileResult.entries.forEach(mergeEntry => {
					const currentEntry = this.entriesMap.get(mergeEntry.id);
					if (!currentEntry) {
						return;
					}

					let mergeEntryWithSelectedKeys = {};
					selectedKeys.forEach(o => mergeEntryWithSelectedKeys[o] = mergeEntry[o]);
					Object.assign(currentEntry, mergeEntryWithSelectedKeys);
				});
			});
		});
	}

	calculateCellClass(id: string, key: string, value: string): string {
		if (!this.originalMap.get(id)) {
			return '';
		}
		const originalValue = this.originalMap.get(id)[key];
		if (!originalValue && value) {
			return 'bg-success-light';
		} else if (originalValue && !value) {
			return 'bg-danger-light';
		} else if (originalValue !== value) {
			return 'bg-info-light';
		}
		return '';
	}

	export() {
		const result = [];
		result.push(this.keys.join('\t'));
		Array.from(this.entriesMap.values()).forEach(entry => result.push(this.keys.map(key => entry[key]).join('\t').trim()));

		const data = `data:text/json;charset=utf-8,${encodeURIComponent(result.join('\n'))}`;

		const downloader = document.createElement('a');
		downloader.setAttribute('href', data);
		downloader.setAttribute('download', 'file.tsv');
		downloader.click();
	}

	calculateDiff(event: any): void {
		this.diffMap.clear();
		const originalMap = new Map();
		const compareMap = new Map();
		const mergeResult = {};
		this.keys.forEach(key => mergeResult[key] = '');
		this.convertFile(event.target.files[0], result => {
			result.entries.forEach(entry => compareMap.set(entry.id, entry));
			Array.from(this.entriesMap.values()).forEach(entry => originalMap.set(entry.id, Object.assign({}, entry)));
			let ids = Array.from(originalMap.keys());
			ids = ids.concat(Array.from(compareMap.keys()).filter(id => !ids.includes(id)));
			ids.forEach(id => {
				const original = originalMap.get(id);
				const compare = compareMap.get(id);
				if (!original || !compare || !this.compareEntries(original, compare)) {
					mergeResult['id'] = id;
					this.diffMap.set(id, {original: original, compare: compare, result: Object.assign({}, mergeResult)});
				}
			});
		});
	}

	merge(): void {
		Array.from(this.diffMap.keys()).forEach(key => {
			const value = this.diffMap.get(key);
			if (value.result) {
				this.entriesMap.set(key, value.result);
			} else {
				this.entriesMap.delete(key);
			}
		});
		this.diffMap.clear();
	}

	calculateDiffHeaderClass(value: any): string {
		if (!value.original) {
			return 'bg-danger-light';
		} else if (!value.compare) {
			return 'bg-success-light';
		}
		return '';
	}

	calculateDiffCellClass(value: any, key: string): string {
		if (!value.original || !value.compare) {
			return '';
		} else if (!value.compare[key]) {
			return 'bg-success-light';
		} else if (!value.original[key]) {
			return 'bg-danger-light';
		} else if (value.original && value.compare && value.original[key] !== value.compare[key]) {
			return 'bg-info-light';
		}
		return '';
	}

	copy(from: any, target: any): void {
		console.log(from);
		console.log(target);
		target.result = Object.assign({}, from);
	}

	private convertFile(file: any, handleLoad: Function): any {
		const reader = new FileReader();
		reader.readAsText(file);
		reader.onload = () => {
			const result = {
				entries: [],
				keys: []
			};
			const content = String(reader.result).replace(/\r/, '').split(/\n/);
			result.keys = content.shift().split(/\t/);
			content.forEach(line => {
				const entry = {};
				const data = line.split(/\t/);
				for (let i = 0; i < result.keys.length; i++) {
					entry[result.keys[i]] = i < data.length ? data[i].trim() : '';
				}
				result.entries.push(entry);
			});
			handleLoad(result);
		};
	}

	private compareEntries(original: any, compare: any): boolean {
		return Object.keys(original).every(key => {
			if (original[key] === compare[key]) {
				return true;
			} else {
				return false;
			}
		});
	}
}
